import { useMemo } from 'react';
import locale from '@glu/locale';
import { Button, EXTRA_SMALL, TERTIARY } from '@glu/buttons-react';
import { Pill } from '@glu/badge-react';
import classNames from 'classnames';
import useStyles from './FilterChips.styles';

export interface IFilterChip {
  [key: string]: any;
  criteriaDisplay: string;
  field: string;
  id: string;
  value?: string;
  nameDisplay: string;
  operator?: 'AND' | 'OR';
}

export interface IFilterChipsProps {
  /** Whether or not to show the filter chip details */
  hidden?: boolean;
  /** Callback to toggle the visibility of the filter chip details */
  toggle(): void;
  /** A flat list of filter chips to show */
  filterChips: IFilterChip[];
  /** Callback to handle when a filter chip is removed */
  onFilterRemove(filterChip: IFilterChip): void;
  /** Callback to handle when a filter chips is selected */
  onFilterSelect?(filterChip: IFilterChip): void;
  /** CSS classes applied to the root element of this component. */
  className?: string;
  /** Html id attribute applied to the root element of this component. */
  htmlId?: string;
}

interface IGroupedFilters {
  id: string;
  nameDisplay: string;
  conditions: IFilterChipsProps['filterChips'];
}

const FilterChips = ({
  className,
  filterChips,
  hidden,
  htmlId,
  onFilterRemove,
  onFilterSelect,
  toggle,
}: IFilterChipsProps) => {
  const classes = useStyles();
  const groupedFilters = useMemo(() => {
    return filterChips.reduce<Record<string, IGroupedFilters>>((combined, filter) => {
      const { field, id, nameDisplay } = filter;
      if (combined[field]) {
        return {
          ...combined,
          [field]: { ...combined[field], conditions: combined[field].conditions.concat(filter) },
        };
      }
      return {
        ...combined,
        [field]: {
          id,
          nameDisplay,
          conditions: [filter],
        },
      };
    }, {});
  }, [filterChips]);

  if (!filterChips || filterChips.length === 0) {
    return null;
  }

  return (
    <div className={classNames(classes.root, className)} id={htmlId}>
      <p className={classes.label}>{locale.get('dataComponents.filters')}</p>
      {Object.values(groupedFilters).map((filter) => (
        <div key={filter.id} className={classes.filterWrap}>
          <p className={classes.display}>
            {filter.nameDisplay}
            <span className={classes.count}>({filter.conditions.length})</span>
          </p>
          {!hidden && (
            <div className={classes.conditionWrap}>
              {filter.conditions.map((condition) => (
                <div
                  key={`condition-${condition.id}-${condition.criteriaDisplay}`}
                  className={classes.condition}
                >
                  <Pill id={condition.id} onRemove={() => onFilterRemove(condition)}>
                    {onFilterSelect ? (
                      <Button
                        className={classes.filterButton}
                        onClick={() => onFilterSelect(condition)}
                        size={EXTRA_SMALL}
                        variant={TERTIARY}
                      >
                        {condition.criteriaDisplay}
                      </Button>
                    ) : (
                      condition.criteriaDisplay
                    )}
                  </Pill>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <Button onClick={toggle} size={EXTRA_SMALL} variant={TERTIARY}>
        {locale.get(hidden ? 'dataComponents.showValues' : 'dataComponents.hideValues')}
      </Button>
    </div>
  );
};

export default FilterChips;
